import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Save, RefreshCw, AlertTriangle, X, CheckCircle, Filter, Calendar, User, Truck, Package, Plus, ArrowLeft, Eye, Trash2, Search, ChevronDown, Download, Printer, MessageSquare, Copy, Check } from 'lucide-react';
import * as XLSX from 'xlsx';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import SortFilterTable, { useClientListGrid } from '../ui/SortFilterTable';
import { GridText, GridClamp, StatusPill, SELECTED_ROW_CLASS } from '../ui/gridCell';
import {
  ALLOCATION_TABLE_PAGE_SIZE,
  pickupPendingColumns,
  pickupAllocatedColumns,
  getPickupOrderCellValue,
} from '../ui/tableColumnConfigs';
import { formMultiSelectTriggerClass } from '../../utils/formControls';

const PickupAllocation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // View states
  const [currentView, setCurrentView] = useState('drivers'); // 'drivers', 'deliveryNote', 'allocation'
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);

  // Drivers data
  const [drivers, setDrivers] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [totalPendingOrders, setTotalPendingOrders] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allocatedOrders, setAllocatedOrders] = useState([]);
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState([]);

  // Modal states
  const [showNewDeliveryNoteModal, setShowNewDeliveryNoteModal] = useState(false);
  const [newDeliveryNote, setNewDeliveryNote] = useState({
    DeliveryDate: new Date().toISOString().split('T')[0],
    VehicleId: '',
    Remarks: ''
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(1); // 1 = Save, 2 = Save and Print Format 1
  const [confirmData, setConfirmData] = useState({
    Date: new Date().toISOString().split('T')[0],
    VehicleId: ''
  });
  const [traceNo, setTraceNo] = useState('');
  const [nextRefNo, setNextRefNo] = useState('');
  const [showPrintDropdown, setShowPrintDropdown] = useState(false);
  const printDropdownRef = useRef(null);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsConfig, setSmsConfig] = useState({
    NeedClientSms: false,
    NeedCustomerSms: false,
    ClientSms: '',
    CustomerSms: ''
  });
  const [smsOrderList, setSmsOrderList] = useState([]);
  const [selectSmsAll, setSelectSmsAll] = useState(false);
  const [copiedKeyword, setCopiedKeyword] = useState('');

  // Selection states
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    upToCurrentDate: false,
    selectedAreas: []
  });
  const [voucherSearchTerm, setVoucherSearchTerm] = useState('');
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Areas data
  const [areas, setAreas] = useState([]);
  const [showAreaDropdown, setShowAreaDropdown] = useState(false);
  const areaDropdownRef = useRef(null);

  // Vehicles data
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    loadData();
    loadAreas();
    loadVehicles();
  }, []);

  // Handle navigation from Delivery Notes page
  useEffect(() => {
    if (location.state && location.state.deliveryNoteId) {
      const { deliveryNoteId, driverId } = location.state;
      // Load the delivery note and show allocated orders
      const loadNoteAndDriver = async () => {
        await loadDriverDeliveryNote(deliveryNoteId);
        setCurrentView('deliveryNote');
        // Find and set the driver if driverId is provided
        if (driverId) {
          const driversList = await MainService.DriversInPickupAllocation();
          if (driversList && Array.isArray(driversList)) {
            const driver = driversList.find(d => d.Id === driverId);
            if (driver) {
              setSelectedDriver(driver);
            }
          }
        }
      };
      loadNoteAndDriver();
      // Clear the state to prevent reloading on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (areaDropdownRef.current && !areaDropdownRef.current.contains(event.target)) {
        setShowAreaDropdown(false);
      }
      if (printDropdownRef.current && !printDropdownRef.current.contains(event.target)) {
        setShowPrintDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      // Load drivers with their allocation counts
      // loadDrivers manages its own loading state
      await loadDrivers();
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    }
  };

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const response = await MainService.DriversInPickupAllocation();
      setDrivers(response);
    } catch (err) {
      console.error('Error loading drivers:', err);
      setError('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const loadAreas = async () => {
    try {
      setLoading(true);
      const response = await MainService.AreasInDropDown();
      setAreas(response || []);
    } catch (err) {
      console.error('Error loading areas:', err);
      setError('Failed to load areas');
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const response = await MainService.VehiclesInDropDown();
      setVehicles(response || []);
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleDriverSelect = async (driver) => {
    setSelectedDriver(driver);
    
    if (!driver.PickUpNoteId > 0) {
      // Set default vehicle if driver has VehicleId
      setNewDeliveryNote(prev => ({
        ...prev,
        VehicleId: driver.VehicleId || ''
      }));
      // Show modal to create new pickup note
      setShowNewDeliveryNoteModal(true);
    } else {
      // Load pickup note for this driver
      // Assuming driver has PickUpNoteId when OrderCount > 0
      if (driver.PickUpNoteId) {
        await loadDriverDeliveryNote(driver.PickUpNoteId);
        setCurrentView('deliveryNote');
      } else {
        setError('Pickup note not found for this driver');
      }
    }
  };

  // Helper function to format date as dd/MM/yyyy
  const formatDateToString = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper function to convert date to YYYY-MM-DD format without timezone issues
  const formatDateToInput = (dateString) => {
    if (!dateString) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse the date and format it
    const date = new Date(dateString);
    // Use local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadDriverDeliveryNote = async (deliveryNoteId) => {
    try {
      setLoading(true);
      const response = await MainService.GetPickupNoteById(deliveryNoteId);
      if (response) {
        setSelectedDeliveryNote(response);
        setAllocatedOrders(response.PickUpNoteDetails || []);
        // Clear selections when reloading
        setSelectedOrderDetails([]);
        
        // Load next RefNo if delivery note doesn't have one yet
        if (!response.RefNo) {
          try {
            const refNoResponse = await MainService.GetNextPickupNoteNo();
            if (refNoResponse) {
              setNextRefNo(refNoResponse);
            }
          } catch (refNoErr) {
            console.error('Error loading next RefNo:', refNoErr);
          }
        } else {
          setNextRefNo(response.RefNo);
        }
      }
    } catch (err) {
      console.error('Error loading delivery note:', err);
      setError('Failed to load delivery note');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeliveryNote = async () => {
    // Validate required fields
    if (!newDeliveryNote.VehicleId) {
      setError('Please select a vehicle');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Format date as dd/MM/yyyy
      const dateString = formatDateToString(newDeliveryNote.DeliveryDate);
      
      // Prepare data for API
      const requestData = {
        DriverId: selectedDriver.Id,
        Remarks: newDeliveryNote.Remarks || '',
        VehicleId: newDeliveryNote.VehicleId,
        DateString: dateString
      };
      
      const response = await MainService.CreatePickupNote(requestData);
      
      if (response && response.Status === 'Success' && response.DeliveryNoteId) {
        // Load the delivery note details
        await loadDriverDeliveryNote(response.DeliveryNoteId);
        
        setShowNewDeliveryNoteModal(false);
        setNewDeliveryNote({
          DeliveryDate: new Date().toISOString().split('T')[0],
          VehicleId: '',
          Remarks: ''
        });
        setSuccess('Delivery note created successfully');
        setCurrentView('deliveryNote');
      } else {
        // Show error from API response
        setError(response?.Status || 'Failed to create delivery note');
      }
      
    } catch (err) {
      console.error('Error creating delivery note:', err);
      setError('Failed to create delivery note');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingOrders = async (applyFilters = false, skip = 0, append = false, totalCount = null) => {
    try {
      if (skip === 0) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      // Prepare request data
      const requestData = {
        isFilter: applyFilters || filtersApplied,
        Areas: (applyFilters || filtersApplied) && filters.selectedAreas.length > 0 ? filters.selectedAreas : null,
        FromDateString: (applyFilters || filtersApplied) ? formatDateToString(filters.dateFrom) : null,
        ToDateString: (applyFilters || filtersApplied) ? (filters.upToCurrentDate ? formatDateToString(new Date()) : formatDateToString(filters.dateTo)) : null,
        IsCurrentDate: (applyFilters || filtersApplied) ? filters.upToCurrentDate : false,
        Skip: skip,
        Take: ALLOCATION_TABLE_PAGE_SIZE
      };
      
      const response = await MainService.OrdersInPickupNoteV2(requestData);
      
      if (response) {
        const total = totalCount !== null ? totalCount : (response.Total || 0);
        const newData = response.Data || [];
        
        if (skip === 0) {
          // First load - set total and initial orders
          setTotalPendingOrders(total);
          setPendingOrders(newData);
          
          // Auto-load next batch if there are more orders
          const loadedCount = newData.length;
          if (loadedCount > 0 && loadedCount === ALLOCATION_TABLE_PAGE_SIZE && skip + ALLOCATION_TABLE_PAGE_SIZE < total) {
            setTimeout(() => {
              loadPendingOrders(applyFilters || filtersApplied, ALLOCATION_TABLE_PAGE_SIZE, true, total);
            }, 100);
          }
        } else {
          // Append next batch - prevent duplicates
          setPendingOrders(prev => {
            // Filter out any duplicates by order ID
            const existingIds = new Set(prev.map(order => order.Id));
            const uniqueNewData = newData.filter(order => !existingIds.has(order.Id));
            const updated = [...prev, ...uniqueNewData];
            const loadedCount = updated.length;
            
            const nextSkip = skip + ALLOCATION_TABLE_PAGE_SIZE;
            if (loadedCount < total && newData.length === ALLOCATION_TABLE_PAGE_SIZE && uniqueNewData.length > 0 && nextSkip < total) {
              setTimeout(() => {
                loadPendingOrders(applyFilters || filtersApplied, nextSkip, true, total);
              }, 100);
            } else if (loadedCount >= total) {
              // If we've reached or exceeded the total, update the total to match actual loaded count
              if (loadedCount > total) {
                setTotalPendingOrders(loadedCount);
              }
            }
            
            return updated;
          });
        }
      } else {
        if (skip === 0) {
          setPendingOrders([]);
          setTotalPendingOrders(0);
        }
      }
    } catch (err) {
      console.error('Error loading pending orders:', err);
      if (skip === 0) {
        setError('Failed to load pending orders');
        setPendingOrders([]);
        setTotalPendingOrders(0);
      }
    } finally {
      if (skip === 0) {
        setLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const handleSwitchToAllocation = async () => {
    // Only a NEW note can have more orders assigned. Once it is in process
    // (Confirmed/Started/etc.), orders must be added via Push Pickup instead.
    if (selectedDeliveryNote?.Status && selectedDeliveryNote.Status !== 'NEW') {
      setError('Pickup Note is already in process. Please use the Push Pickup feature to add more orders (allowed only while the note is not finished).');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setSelectedOrderIds([]);
      setVoucherSearchTerm('');
      setFiltersApplied(false);
      setPendingOrders([]);
      setTotalPendingOrders(0);
      await loadPendingOrders(false);
      setCurrentView('allocation');
    } catch (err) {
      console.error('Error switching to allocation view:', err);
      setError('Failed to load pending orders');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDeliveryNote = () => {
    setCurrentView('deliveryNote');
  };

  const handleBackToDrivers = () => {
    setCurrentView('drivers');
    setSelectedDriver(null);
    setSelectedDeliveryNote(null);
    setAllocatedOrders([]);
    setDriverSearchTerm('');
    setOrderSearchTerm('');
    setSelectedOrderDetails([]);
    loadDrivers();
  };

  const handleDeleteOrder = (detail) => {
    setOrderToDelete(detail);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOrder = async () => {
    // If bulk delete (orderToDelete is null but selectedOrderDetails has items)
    if (!orderToDelete && selectedOrderDetails.length > 0) {
      await confirmBulkDelete();
      return;
    }

    // Single delete
    if (!orderToDelete || !selectedDeliveryNote) {
      setError('Invalid order or delivery note');
      setDeleteDialogOpen(false);
      return;
    }

    // Check if delivery note status is FINISHED
    if (selectedDeliveryNote.Status === 'FINISHED') {
      setError('Delivery Completed - Operation Not Possible!');
      setDeleteDialogOpen(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const deliveryNoteDetails = [{
        DeliveryNoteId: selectedDeliveryNote.Id,
        Id: orderToDelete.Id,
        OrderId: orderToDelete.OrderId
      }];

      const response = await MainService.removePickupDetails(deliveryNoteDetails);
      
      if (response) {
        setSuccess('Order removed from delivery note successfully');
        // Reload the delivery note to refresh the list
        await loadDriverDeliveryNote(selectedDeliveryNote.Id);
        setDeleteDialogOpen(false);
        setOrderToDelete(null);
      } else {
        setError('Something went wrong');
      }
    } catch (err) {
      console.error('Error removing order:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setOrderToDelete(null);
  };

  const toggleOrderDetailSelection = (detailId) => {
    setSelectedOrderDetails(prev => {
      if (prev.includes(detailId)) {
        return prev.filter(id => id !== detailId);
      } else {
        return [...prev, detailId];
      }
    });
  };

  const toggleSelectAllOrderDetails = () => {
    if (selectedOrderDetails.length === filteredAllocatedOrders.length) {
      setSelectedOrderDetails([]);
    } else {
      setSelectedOrderDetails(filteredAllocatedOrders.map(detail => detail.Id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedOrderDetails.length === 0) {
      setError('Please select orders to delete');
      return;
    }

    if (!selectedDeliveryNote) {
      setError('Invalid delivery note');
      return;
    }

    // Check if delivery note status is FINISHED
    if (selectedDeliveryNote.Status === 'FINISHED') {
      setError('Delivery Completed - Operation Not Possible!');
      return;
    }

    setDeleteDialogOpen(true);
    // Set orderToDelete to null to indicate bulk delete
    setOrderToDelete(null);
  };

  const confirmBulkDelete = async () => {
    if (!selectedDeliveryNote) {
      setError('Invalid delivery note');
      setDeleteDialogOpen(false);
      return;
    }

    // Check if delivery note status is FINISHED
    if (selectedDeliveryNote.Status === 'FINISHED') {
      setError('Delivery Completed - Operation Not Possible!');
      setDeleteDialogOpen(false);
      return;
    }

    if (selectedOrderDetails.length === 0) {
      setError('Please select orders to delete');
      setDeleteDialogOpen(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Get selected order details
      const selectedDetails = allocatedOrders.filter(detail => 
        selectedOrderDetails.includes(detail.Id)
      );

      const deliveryNoteDetails = selectedDetails.map(detail => ({
        DeliveryNoteId: selectedDeliveryNote.Id,
        Id: detail.Id,
        OrderId: detail.OrderId
      }));

      const response = await MainService.removePickupDetails(deliveryNoteDetails);
      
      if (response) {
        setSuccess(`${selectedOrderDetails.length} order(s) removed from delivery note successfully`);
        // Reload the delivery note to refresh the list
        await loadDriverDeliveryNote(selectedDeliveryNote.Id);
        setSelectedOrderDetails([]);
        setDeleteDialogOpen(false);
      } else {
        setError('Something went wrong');
      }
    } catch (err) {
      console.error('Error removing orders:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const exportToExcel = () => {
    if (filteredAllocatedOrders.length === 0) {
      setError('No data to export');
      return;
    }

    try {
      const dataToExport = filteredAllocatedOrders.map((detail, index) => {
        const order = detail.Order || {};
        const status = detail.Status || {};
        const clientAddress = order.ClientAddress || {};
        const location = clientAddress.Location || {};
        const area = location.Area || {};
        
        return {
          'S.No': index + 1,
          'Voucher No': order.VoucherNo || '',
          'Pickup Date': order.PickupDate ? formatDateToString(order.PickupDate) : '',
          'Delivery Date': order.DeliveryDate ? formatDateToString(order.DeliveryDate) : '',
          'Client': order.Client?.Name || '',
          'Client No': order.Client?.MobileNo1 || '',
          'Client Address': clientAddress.Address || '',
          'Client Area': area.Name || '',
          'Client Location': location.Name || '',
          'Status': status.Name || '',
          'Amount': order.ClientAmount ? parseFloat(order.ClientAmount).toFixed(2) : '',
          'Attempts': '-',
          'Remarks': order.Remarks || '',
          'Store Ref No': order.StoreRefNo || ''
        };
      });

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Allocated Orders');
      const fileName = `DeliveryAllocation_${selectedDeliveryNote?.RefNo || 'Orders'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setSuccess('Data exported to Excel successfully');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError('Failed to export to Excel');
    }
  };

  const handleAllocateOrders = async () => {
    if (selectedOrderIds.length === 0) {
      setError('Please select at least one order to allocate');
      return;
    }

    if (!selectedDeliveryNote || !selectedDeliveryNote.Id) {
      setError('Delivery note not found');
      return;
    }

    try {
      setLoading(true);
      
      // Get selected orders from pending orders
      const selectedOrders = pendingOrders.filter(order => selectedOrderIds.includes(order.Id));
      
      if (selectedOrders.length === 0) {
        setError('Please select orders to allocate');
        setLoading(false);
        return;
      }

      // Create pickup note details array
      const deliveryNoteDetails = selectedOrders.map(order => ({
        PickupNoteId: selectedDeliveryNote.Id,
        OrderId: order.Id,
        StoreRefNo: order.StoreRefNo || ''
      }));

      // Call API to assign orders
      const response = await MainService.assignOrdersToPN(deliveryNoteDetails);
      
      if (response) {
        setSuccess(`${selectedOrders.length} order(s) allocated successfully`);
        setSelectedOrderIds([]);
        
        // Refresh pending orders
        await loadPendingOrders(filtersApplied);
        
        // Reload drivers to update order counts
        await loadDrivers();
        
        // Refresh allocated orders in the delivery note view
        if (selectedDeliveryNote.Id) {
          await loadDriverDeliveryNote(selectedDeliveryNote.Id);
        }
        
        // Switch back to delivery note view
        setCurrentView('deliveryNote');
      } else {
        setError('Something went wrong');
      }
      
    } catch (err) {
      console.error('Error allocating orders:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrderIds.length === pendingOrders.length && pendingOrders.length > 0) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(pendingOrders.map(order => order.Id));
    }
  };

  const toggleAreaSelection = (areaId) => {
    setFilters(prev => ({
      ...prev,
      selectedAreas: prev.selectedAreas.includes(areaId)
        ? prev.selectedAreas.filter(id => id !== areaId)
        : [...prev.selectedAreas, areaId]
    }));
  };

  const handleUpToCurrentDateChange = (checked) => {
    setFilters(prev => ({
      ...prev,
      upToCurrentDate: checked,
      dateTo: checked ? new Date().toISOString().split('T')[0] : prev.dateTo
    }));
  };

  const handleApplyFilters = async () => {
    try {
      setLoading(true);
      setFiltersApplied(true);
      await loadPendingOrders(true);
    } catch (err) {
      console.error('Error applying filters:', err);
      setError('Failed to apply filters');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      dateFrom: new Date().toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
      upToCurrentDate: false,
      selectedAreas: []
    });
    setVoucherSearchTerm('');
    setFiltersApplied(false);
    setTotalPendingOrders(0);
    loadPendingOrders(false);
  };

  const handleSmsClientSwitch = () => {
    setSmsConfig(prev => ({ ...prev, NeedClientSms: !prev.NeedClientSms }));
  };

  const handleSmsCustomerSwitch = () => {
    setSmsConfig(prev => ({ ...prev, NeedCustomerSms: !prev.NeedCustomerSms }));
  };

  const handleSelectOrderForSMS = (order) => {
    setSmsOrderList(prev => {
      const updated = prev.map(item => 
        item.Id === order.Id 
          ? { ...item, IsSmsChecked: !item.IsSmsChecked }
          : item
      );
      // Update selectSmsAll based on all orders being selected
      const allSelected = updated.length > 0 && updated.every(item => item.IsSmsChecked);
      setSelectSmsAll(allSelected);
      return updated;
    });
  };

  const handleSelectAllSms = () => {
    setSelectSmsAll(prev => {
      const newValue = !prev;
      setSmsOrderList(prevList => 
        prevList.map(item => ({ ...item, IsSmsChecked: newValue }))
      );
      return newValue;
    });
  };

  const handleSubmitSms = () => {
    // TODO: Implement SMS submission
    setShowSmsModal(false);
    setSuccess('SMS configuration saved');
  };

  const handleCopyKeyword = (keyword) => {
    navigator.clipboard.writeText(keyword).then(() => {
      setCopiedKeyword(keyword);
      setTimeout(() => {
        setCopiedKeyword('');
      }, 2000);
    });
  };

  const handleSaveClick = (action) => {
    // Set default vehicle from delivery note if available
    const defaultVehicleId = selectedDeliveryNote?.VehicleId || selectedDriver?.VehicleId || '';
    setConfirmData({
      Date: formatDateToInput(selectedDeliveryNote?.Date),
      VehicleId: defaultVehicleId
    });
    setConfirmAction(action);
    setShowConfirmModal(true);
  };

  const handleConfirmDeliveryNote = async () => {
    if (!confirmData.VehicleId) {
      setError('Please select a vehicle');
      return;
    }

    if (!selectedDeliveryNote || !selectedDeliveryNote.Id) {
      setError('Delivery note not found');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Get next delivery note number
      const traceNoResponse = await MainService.GetNextPickupNoteNo();
      const refNo = traceNoResponse || '';

      // Parse date
      const date = new Date(confirmData.Date);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      // Get selected order IDs for SMS (from SMS order list where IsSmsChecked is true)
      const selectedOrderIds = smsOrderList
        .filter(order => order.IsSmsChecked)
        .map(order => order.Order?.Id)
        .filter(id => id != null);

      // Prepare confirmation data
      const confirmRequestData = {
        Id: selectedDeliveryNote.Id,
        DriverId: selectedDriver?.Id || selectedDeliveryNote.DriverId,
        VehicleId: confirmData.VehicleId,
        Day: day,
        Month: month,
        Year: year,
        RefNo: refNo,
        ClientSms: smsConfig.ClientSms || '',
        CustomerSms: smsConfig.CustomerSms || '',
        NeedCustomerSms: smsConfig.NeedCustomerSms || false,
        NeedClientSms: smsConfig.NeedClientSms || false,
        OrderIds: selectedOrderIds
      };

      const response = await MainService.ConfirmPickupNote(confirmRequestData);

      if (response && response.Status === 'Success') {
        setSuccess('Pickup note confirmed successfully');
        setShowConfirmModal(false);
        
        const deliveryNoteId = response.DeliveryNoteId || selectedDeliveryNote.Id;
        
        // Reload drivers
        await loadDrivers();

        // Handle print action
        if (confirmAction === 2) {
          try {
            const printResponse = await MainService.PrintPickUpAllocation(deliveryNoteId);

            if (printResponse && printResponse != null) {
              // Open print file in new window
              const printUrl = `${getApiBaseUrl()}ReportFiles/${printResponse}`;
              window.open(printUrl, '_blank');
              setSuccess('Delivery note confirmed and printing...');
            } else {
              setError('Print Error');
            }
          } catch (printErr) {
            console.error('Error printing delivery note:', printErr);
            setError('Print Error');
          }
        }

        // Reset to drivers screen after save/print
        setSelectedDriver(null);
        setSelectedDeliveryNote(null);
        setAllocatedOrders([]);
        setSelectedOrderDetails([]);
        setNextRefNo('');
        setCurrentView('drivers');
      } else {
        setError(response?.Status || 'Failed to confirm delivery note');
      }
    } catch (err) {
      console.error('Error confirming delivery note:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Auto-dismiss messages
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Filter drivers based on search term
  const filteredDrivers = drivers.filter(driver => {
    if (!driverSearchTerm) return true;
    const searchLower = driverSearchTerm.toLowerCase();
    return (
      (driver.Name || '').toLowerCase().includes(searchLower) ||
      (driver.MobileNo || '').toLowerCase().includes(searchLower)
    );
  });

  // Filter allocated orders based on search term
  const filteredAllocatedOrders = allocatedOrders.filter(detail => {
    if (!orderSearchTerm) return true;
    const searchLower = orderSearchTerm.toLowerCase();
    const order = detail.Order || {};
    const clientAddress = order.ClientAddress || {};
    const location = clientAddress.Location || {};
    const area = location.Area || {};
    
    return (
      (order.VoucherNo || '').toLowerCase().includes(searchLower) ||
      (order.Client?.Name || '').toLowerCase().includes(searchLower) ||
      (order.Client?.MobileNo1 || '').toLowerCase().includes(searchLower) ||
      (clientAddress.Address || '').toLowerCase().includes(searchLower) ||
      (area.Name || '').toLowerCase().includes(searchLower) ||
      (location.Name || '').toLowerCase().includes(searchLower) ||
      String(order.StoreRefNo ?? '').toLowerCase().includes(searchLower)
    );
  });

  const pickupAllocatedCols = useMemo(() => pickupAllocatedColumns(), []);
  const pickupPendingCols = useMemo(() => pickupPendingColumns(), []);

  const getPickupCell = useCallback(
    (row, field) => getPickupOrderCellValue(row, field, formatDateToString),
    []
  );

  const renderPickupOrderCell = useCallback((row, column) => {
    const order = row.Order || row;
    const status = row.Status || order.Status || {};

    if (column.field === 'status') {
      return <StatusPill status={status} />;
    }
    if (column.field === 'clientAddress' || column.field === 'remarks') {
      return <GridClamp value={getPickupOrderCellValue(row, column.field, formatDateToString)} />;
    }
    if (column.field === 'voucher') {
      return <GridText value={getPickupOrderCellValue(row, column.field, formatDateToString)} className="font-medium text-gray-900" />;
    }
    if (column.field === 'amount') {
      return <GridText value={getPickupOrderCellValue(row, column.field, formatDateToString)} className="font-medium text-gray-900" />;
    }
    if (column.field === 'actions') {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteOrder(row);
          }}
          disabled={selectedDeliveryNote?.Status === 'FINISHED'}
          className="text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed"
          title={selectedDeliveryNote?.Status === 'FINISHED' ? 'Cannot delete - Delivery Completed' : 'Delete order'}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      );
    }
    return <GridText value={getPickupOrderCellValue(row, column.field, formatDateToString)} />;
  }, [selectedDeliveryNote]);

  const allocatedOrderGrid = useClientListGrid({
    columns: pickupAllocatedCols,
    rows: filteredAllocatedOrders,
    getCellValue: getPickupCell,
    initialPageSize: ALLOCATION_TABLE_PAGE_SIZE,
  });

  const pendingOrderGrid = useClientListGrid({
    columns: pickupPendingCols,
    rows: pendingOrders,
    getCellValue: getPickupCell,
    initialPageSize: ALLOCATION_TABLE_PAGE_SIZE,
  });

  // Auto-select and scroll to order when voucher number matches exactly
  useEffect(() => {
    if (voucherSearchTerm && currentView === 'allocation' && pendingOrders.length > 0) {
      // Find exact match by VoucherNo only (case-insensitive)
      const searchTerm = voucherSearchTerm.trim();
      if (searchTerm) {
        const matchedOrder = pendingOrders.find(order => 
          order.VoucherNo && String(order.VoucherNo).toLowerCase() === searchTerm.toLowerCase()
        );
        
        if (matchedOrder) {
          // Auto-select the matched order if not already selected
          setSelectedOrderIds(prev => {
            if (!prev.includes(matchedOrder.Id)) {
              return [...prev, matchedOrder.Id];
            }
            return prev;
          });

          // Scroll to the matched row
          setTimeout(() => {
            const rowElement = document.getElementById(`order-row-${matchedOrder.Id}`);
            if (rowElement) {
              rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Highlight the row briefly
              rowElement.classList.add('bg-yellow-100');
              setTimeout(() => {
                rowElement.classList.remove('bg-yellow-100');
              }, 1000);
            }
          }, 100);

          // Clear the input after match is found
          setVoucherSearchTerm('');
        }
      }
    }
  }, [voucherSearchTerm, pendingOrders, currentView]);

  // Render Driver Selection View
  const renderDriverSelection = () => (
    <>
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{getPageHeading(language, 'pickupAllocation')}</h1>
            <p className="text-sm text-gray-500 mt-1">Select a driver to view or create delivery allocations</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Search Bar */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by driver name or mobile number..."
                value={driverSearchTerm}
                onChange={(e) => setDriverSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </div>
            {loading && <RefreshCw className="w-5 h-5 text-primary animate-spin" />}
            {!loading && (
              <button
                onClick={loadData}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Driver Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredDrivers.map((driver) => (
          <div
            key={driver.Id}
            onClick={() => handleDriverSelect(driver)}
            className={`rounded-lg shadow-sm p-6 hover:shadow-md transition-all cursor-pointer relative ${
              driver.PickUpNoteId > 0 
                ? 'animate-border-run' 
                : 'bg-white border border-gray-200 hover:border-primary'
            }`}
          >
            <div className={`flex flex-col items-center text-center ${driver.PickUpNoteId > 0 ? 'bg-white rounded-lg' : ''}`}>
              {/* Driver Photo */}
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-green-600 flex items-center justify-center text-white text-2xl font-bold mb-3">
                {driver.Photo ? (
                  <img src={driver.Photo} alt={driver.Name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span>{driver.Name?.charAt(0) || 'D'}</span>
                )}
              </div>
              
              {/* Driver Name */}
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{driver.Name}</h3>
              
              {/* Allocation Count Badge */}
              <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                !(driver.PickUpNoteId > 0)
                  ? 'bg-gray-100 text-gray-600' 
                  : 'bg-green-100 text-green-700'
              }`}>
                {!(driver.PickUpNoteId > 0) ? (
                  <span>No Allocations</span>
                ) : (
                  <span>{driver.OrderCount} Order{driver.OrderCount > 1 ? 's' : ''} Allocated</span>
                )}
              </div>

              {/* Action Hint */}
              <p className="text-xs text-gray-500 mt-3">
                {!(driver.PickUpNoteId > 0) ? 'Click to create pickup note' : 'Click to view allocations'}
              </p>
            </div>
          </div>
        ))}
      </div>

      {filteredDrivers.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Truck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            {driverSearchTerm ? 'No Drivers Found' : 'No Drivers Available'}
          </p>
          <p className="text-sm text-gray-500">
            {driverSearchTerm 
              ? 'No drivers found matching your search. Try a different search term.' 
              : 'Please add drivers to the system first'}
          </p>
        </div>
      )}
    </>
  );

  // Render Delivery Note View
  const renderDeliveryNoteView = () => (
    <>
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToDrivers}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-gray-900">{getPageHeading(language, 'pickupNote')}</h1>
                {(selectedDeliveryNote?.RefNo || nextRefNo) && (
                  <span className="text-lg font-semibold text-gray-600">
                    ({selectedDeliveryNote?.RefNo || nextRefNo})
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Driver: {selectedDeliveryNote?.Driver?.Name || selectedDriver?.Name} • {allocatedOrders.length} orders allocated
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSaveClick(1)}
              disabled={loading || !selectedDeliveryNote || allocatedOrders.length === 0}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              <span>Save</span>
            </button>
            <div className="relative" ref={printDropdownRef}>
              <button
                onClick={() => setShowPrintDropdown(!showPrintDropdown)}
                disabled={loading || !selectedDeliveryNote || allocatedOrders.length === 0}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                <span>Save and Print</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {showPrintDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <button
                    onClick={() => {
                      handleSaveClick(2);
                      setShowPrintDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    <span>Format 1</span>
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={handleSwitchToAllocation}
              disabled={loading || (selectedDeliveryNote?.Status && selectedDeliveryNote.Status !== 'NEW')}
              title={selectedDeliveryNote?.Status && selectedDeliveryNote.Status !== 'NEW' ? 'Pickup Note in process — use Push Pickup to add more orders' : ''}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Add Orders</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/home/pickup/notes')}
              title="View Pickup Notes"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Allocated Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Allocated Orders</h2>
          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders..."
                value={orderSearchTerm}
                onChange={(e) => setOrderSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </div>
            <button
              onClick={exportToExcel}
              disabled={filteredAllocatedOrders.length === 0}
              className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              title="Export to Excel"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                // Initialize SMS order list from allocated orders
                const orders = allocatedOrders.map(detail => ({
                  ...detail,
                  IsSmsChecked: false
                }));
                setSmsOrderList(orders);
                setSelectSmsAll(false);
                // Set default SMS templates
                const defaultSms = "Dear  @Name,  Your order @VoucherNo  will deliver today. for more details contact us or contact delivery person @DriverName - @DriverPhoneNo.";
                setSmsConfig({
                  NeedClientSms: false,
                  NeedCustomerSms: false,
                  ClientSms: defaultSms,
                  CustomerSms: defaultSms
                });
                setShowSmsModal(true);
              }}
              disabled={filteredAllocatedOrders.length === 0}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              title="SMS Configuration"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={selectedOrderDetails.length === 0 || selectedDeliveryNote?.Status === 'FINISHED'}
              className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              title={selectedDeliveryNote?.Status === 'FINISHED' ? 'Cannot delete - Delivery Completed' : selectedOrderDetails.length === 0 ? 'Select orders to delete' : `Delete ${selectedOrderDetails.length} selected order(s)`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <SortFilterTable
          columns={pickupAllocatedCols}
          grid={allocatedOrderGrid}
          rows={filteredAllocatedOrders}
          getCellValue={getPickupCell}
          renderCell={renderPickupOrderCell}
          emptyText={orderSearchTerm ? 'No orders match your search criteria' : 'No orders allocated yet'}
          minWidth="1400px"
          maxHeight={560}
          showPagination
          tableClassName="w-full table-fixed"
          getRowClassName={(detail) => (selectedOrderDetails.includes(detail.Id) ? SELECTED_ROW_CLASS : '')}
          leadingHeader={
            <th className="w-[3%] px-4 py-2 text-left">
              <input
                type="checkbox"
                className="rounded"
                checked={filteredAllocatedOrders.length > 0 && selectedOrderDetails.length === filteredAllocatedOrders.length}
                onChange={toggleSelectAllOrderDetails}
              />
            </th>
          }
          renderLeadingCell={(detail) => (
            <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                className="rounded"
                checked={selectedOrderDetails.includes(detail.Id)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleOrderDetailSelection(detail.Id);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </td>
          )}
        />
      </div>
    </>
  );

  // Render Order Allocation View
  const renderAllocationView = () => (
    <>
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToDeliveryNote}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{getPageHeading(language, 'pickupAllocationAllocate')}</h1>
              <p className="text-sm text-gray-500 mt-1">
                Driver: <span className="font-medium text-gray-700">{selectedDriver?.Name || '-'}</span>
                {(selectedDeliveryNote?.RefNo || nextRefNo) && (
                  <> • Pickup Note: <span className="font-medium text-gray-700">{selectedDeliveryNote?.RefNo || nextRefNo}</span></>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAllocateOrders}
              disabled={selectedOrderIds.length === 0 || loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Allocating...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Allocate {selectedOrderIds.length > 0 ? `(${selectedOrderIds.length})` : ''}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-primary" />
          Filters
        </h2>
        <div className="space-y-4">
          {/* Single Row */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                disabled={filters.upToCurrentDate}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50"
              />
              <label className="mt-2 flex items-center gap-2 cursor-pointer text-xs font-normal text-gray-600">
                <input
                  type="checkbox"
                  checked={filters.upToCurrentDate}
                  onChange={(e) => handleUpToCurrentDateChange(e.target.checked)}
                  className="rounded"
                />
                <span>Up to current date</span>
              </label>
            </div>
            <div className="md:col-span-4 relative" ref={areaDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Areas (Multi-select)</label>
              <button
                type="button"
                onClick={() => setShowAreaDropdown(!showAreaDropdown)}
                className={formMultiSelectTriggerClass}
              >
                <span className={filters.selectedAreas.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
                  {filters.selectedAreas.length === 0
                    ? 'Select Areas'
                    : `${filters.selectedAreas.length} area(s) selected`}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>
              
              {showAreaDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {areas.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">No areas available</div>
                  ) : (
                    areas.map((area) => (
                      <label
                        key={area.Id}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.selectedAreas.includes(area.Id)}
                          onChange={() => toggleAreaSelection(area.Id)}
                          className="rounded mr-2"
                        />
                        <span className="text-sm text-gray-700">{area.Name}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="md:col-span-4 self-start">
              <span className="block text-sm font-medium mb-1 invisible" aria-hidden="true">Actions</span>
              <div className="flex items-center gap-2">
              <button
                onClick={handleClearFilters}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
              <button
                onClick={handleApplyFilters}
                disabled={loading}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Apply Filters
                  </>
                )}
              </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Pending Orders</h2>
            {totalPendingOrders > 0 && (
              <span className="text-sm text-gray-500 font-normal">
                ({totalPendingOrders} {totalPendingOrders === 1 ? 'order' : 'orders'})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search/Scan Voucher No"
                value={voucherSearchTerm}
                onChange={(e) => setVoucherSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </div>
            {pendingOrders.length > 0 && (
              <button
                onClick={toggleSelectAll}
                className="text-sm text-primary hover:text-green-700 font-medium"
              >
                {selectedOrderIds.length === pendingOrders.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
        </div>
        
        <SortFilterTable
          columns={pickupPendingCols}
          grid={pendingOrderGrid}
          rows={pendingOrders}
          getCellValue={getPickupCell}
          renderCell={renderPickupOrderCell}
          emptyText="No pending orders"
          minWidth="1400px"
          maxHeight={560}
          showPagination
          tableClassName="w-full table-fixed"
          onRowClick={(order) => toggleOrderSelection(order.Id)}
          getRowClassName={(order) => (selectedOrderIds.includes(order.Id) ? SELECTED_ROW_CLASS : '')}
          leadingHeader={
            <th className="w-[3%] px-4 py-2 text-left">
              <input
                type="checkbox"
                className="rounded"
                checked={pendingOrders.length > 0 && selectedOrderIds.length === pendingOrders.length}
                onChange={toggleSelectAll}
              />
            </th>
          }
          renderLeadingCell={(order) => (
            <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                className="rounded"
                checked={selectedOrderIds.includes(order.Id)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleOrderSelection(order.Id);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </td>
          )}
          footerExtra={
            isLoadingMore ? (
              <tr>
                <td colSpan={pendingOrderGrid.colSpan + 1} className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                    <span>Loading more orders...</span>
                  </div>
                </td>
              </tr>
            ) : null
          }
        />
      </div>
    </>
  );

  return (
    <>
      <div className="space-y-6">
        {/* Toast Messages */}
        {error && (
          <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{success}</span>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Render appropriate view based on state */}
        {currentView === 'drivers' && renderDriverSelection()}
        {currentView === 'deliveryNote' && renderDeliveryNoteView()}
        {currentView === 'allocation' && renderAllocationView()}
      </div>

      {/* New Delivery Note Modal */}
      {showNewDeliveryNoteModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative z-[100000]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Pickup Note</h3>
              <button
                onClick={() => {
                  setShowNewDeliveryNoteModal(false);
                  setSelectedDriver(null);
                  setNewDeliveryNote({
                    DeliveryDate: new Date().toISOString().split('T')[0],
                    VehicleId: '',
                    Remarks: ''
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Driver</label>
                <input
                  type="text"
                  value={selectedDriver?.Name || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date</label>
                <input
                  type="date"
                  value={newDeliveryNote.DeliveryDate}
                  onChange={(e) => setNewDeliveryNote(prev => ({ ...prev, DeliveryDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle <span className="text-red-500">*</span>
                </label>
                <select
                  value={newDeliveryNote.VehicleId}
                  onChange={(e) => setNewDeliveryNote(prev => ({ ...prev, VehicleId: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">Select Vehicle</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.Id} value={vehicle.Id}>
                      {vehicle.Number || vehicle.Name || `Vehicle ${vehicle.Id}`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (Optional)</label>
                <textarea
                  rows="3"
                  value={newDeliveryNote.Remarks}
                  onChange={(e) => setNewDeliveryNote(prev => ({ ...prev, Remarks: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                  placeholder="Enter any remarks"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNewDeliveryNoteModal(false);
                  setSelectedDriver(null);
                  setNewDeliveryNote({
                    DeliveryDate: new Date().toISOString().split('T')[0],
                    VehicleId: '',
                    Remarks: ''
                  });
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleCreateDeliveryNote}
                disabled={loading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Create & Continue</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirm Delivery Note Modal */}
      {showConfirmModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative z-[100000]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {confirmAction === 1 ? 'Save Pickup Note' : 'Save and Print - Format 1'}
              </h3>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmData({
                    Date: new Date().toISOString().split('T')[0],
                    VehicleId: ''
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={confirmData.Date}
                  onChange={(e) => setConfirmData(prev => ({ ...prev, Date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle <span className="text-red-500">*</span>
                </label>
                <select
                  value={confirmData.VehicleId}
                  onChange={(e) => setConfirmData(prev => ({ ...prev, VehicleId: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">Select Vehicle</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.Id} value={vehicle.Id}>
                      {vehicle.Number || vehicle.Name || `Vehicle ${vehicle.Id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmData({
                    Date: new Date().toISOString().split('T')[0],
                    VehicleId: ''
                  });
                }}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleConfirmDeliveryNote}
                disabled={loading || !confirmData.VehicleId}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Confirming...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>{confirmAction === 1 ? 'Save' : 'Save and Print - Format 1'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative z-[100000]">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 -mx-6 -mt-6 rounded-t-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-900">Confirm Delete</h3>
              </div>
            </div>
            <div className="px-0 py-4">
              {orderToDelete ? (
                <>
                  <p className="text-gray-700 mb-2">
                    Are you sure you want to remove order <strong>"{orderToDelete?.Order?.VoucherNo || 'N/A'}"</strong> from this delivery note?
                  </p>
                  <p className="text-sm text-gray-500">
                    This action will remove the order from the delivery note. You can add it back later if needed.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-700 mb-2">
                    Are you sure you want to remove <strong>{selectedOrderDetails.length} order(s)</strong> from this delivery note?
                  </p>
                  <p className="text-sm text-gray-500">
                    This action will remove the selected orders from the delivery note. You can add them back later if needed.
                  </p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={cancelDelete}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteOrder}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* SMS Configuration Modal */}
      {showSmsModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sms-config-title"
        >
          <div className="flex h-[90vh] max-h-[90vh] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 id="sms-config-title" className="text-xl font-semibold text-gray-900">SMS Configuration</h3>
              <button
                type="button"
                onClick={() => {
                  setShowSmsModal(false);
                  setSmsConfig({
                    NeedClientSms: false,
                    NeedCustomerSms: false,
                    ClientSms: '',
                    CustomerSms: ''
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close SMS configuration"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-6 py-4">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
              {/* Left Column - SMS Configuration */}
              <div className="space-y-6">
                {/* Client SMS Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">SMS for Client?</p>
                    <button
                      onClick={handleSmsClientSwitch}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        smsConfig.NeedClientSms
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {smsConfig.NeedClientSms ? 'Yes' : 'No'}
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Client SMS</label>
                    <textarea
                      value={smsConfig.ClientSms}
                      onChange={(e) => setSmsConfig(prev => ({ ...prev, ClientSms: e.target.value }))}
                      rows={5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      placeholder="Enter client SMS template..."
                    />
                  </div>
                </div>

                {/* Customer SMS Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">SMS for Customer?</p>
                    <button
                      onClick={handleSmsCustomerSwitch}
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        smsConfig.NeedCustomerSms
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {smsConfig.NeedCustomerSms ? 'Yes' : 'No'}
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer SMS</label>
                    <textarea
                      value={smsConfig.CustomerSms}
                      onChange={(e) => setSmsConfig(prev => ({ ...prev, CustomerSms: e.target.value }))}
                      rows={5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      placeholder="Enter customer SMS template..."
                    />
                  </div>
                </div>

                {/* Keywords Table */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                    <p className="text-xs font-medium text-gray-700">Click on any keyword name to copy</p>
                  </div>
                  <div className="bg-white p-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div 
                        onClick={() => handleCopyKeyword('@VoucherNo')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Voucher No</span>
                          <span className="text-xs text-gray-500 font-mono">@VoucherNo</span>
                        </div>
                        {copiedKeyword === '@VoucherNo' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@DriverName')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Driver Name</span>
                          <span className="text-xs text-gray-500 font-mono">@DriverName</span>
                        </div>
                        {copiedKeyword === '@DriverName' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@DriverPhoneNo')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Driver Phone No</span>
                          <span className="text-xs text-gray-500 font-mono">@DriverPhoneNo</span>
                        </div>
                        {copiedKeyword === '@DriverPhoneNo' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@COD')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Money</span>
                          <span className="text-xs text-gray-500 font-mono">@COD</span>
                        </div>
                        {copiedKeyword === '@COD' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@DeliveryDate')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Delivery Date</span>
                          <span className="text-xs text-gray-500 font-mono">@DeliveryDate</span>
                        </div>
                        {copiedKeyword === '@DeliveryDate' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@Name')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Salutation</span>
                          <span className="text-xs text-gray-500 font-mono">@Name</span>
                        </div>
                        {copiedKeyword === '@Name' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@ClientName')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Client Name</span>
                          <span className="text-xs text-gray-500 font-mono">@ClientName</span>
                        </div>
                        {copiedKeyword === '@ClientName' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@CustomerName')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Customer Name</span>
                          <span className="text-xs text-gray-500 font-mono">@CustomerName</span>
                        </div>
                        {copiedKeyword === '@CustomerName' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@ClientArea')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Client Area</span>
                          <span className="text-xs text-gray-500 font-mono">@ClientArea</span>
                        </div>
                        {copiedKeyword === '@ClientArea' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@ClientLoc')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Client Location</span>
                          <span className="text-xs text-gray-500 font-mono">@ClientLoc</span>
                        </div>
                        {copiedKeyword === '@ClientLoc' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@ClientEmirate')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Client Emirate</span>
                          <span className="text-xs text-gray-500 font-mono">@ClientEmirate</span>
                        </div>
                        {copiedKeyword === '@ClientEmirate' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@CustomerArea')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Customer Area</span>
                          <span className="text-xs text-gray-500 font-mono">@CustomerArea</span>
                        </div>
                        {copiedKeyword === '@CustomerArea' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@CustomerLocation')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Customer Location</span>
                          <span className="text-xs text-gray-500 font-mono">@CustomerLocation</span>
                        </div>
                        {copiedKeyword === '@CustomerLocation' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                      <div 
                        onClick={() => handleCopyKeyword('@CustomerEmirate')}
                        className="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Customer Emirate</span>
                          <span className="text-xs text-gray-500 font-mono">@CustomerEmirate</span>
                        </div>
                        {copiedKeyword === '@CustomerEmirate' ? (
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Copied
                          </span>
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Order Selection */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">Select Orders</p>
                  <button
                    type="button"
                    onClick={handleSelectAllSms}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                      selectSmsAll
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    {selectSmsAll ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">Select</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">Voucher No</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">Client</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">Customer</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">Location</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">Emirate</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">Area</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">Attempts</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {smsOrderList.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                            No orders available
                          </td>
                        </tr>
                      ) : (
                        smsOrderList.map((orderDetail) => {
                          const order = orderDetail.Order;
                          if (!order) return null;
                          return (
                            <tr key={orderDetail.Id} className="hover:bg-gray-50">
                              <td className="px-3 py-2">
                                <button
                                  onClick={() => handleSelectOrderForSMS(orderDetail)}
                                  className={`p-1.5 rounded transition-colors ${
                                    orderDetail.IsSmsChecked
                                      ? 'bg-green-600 text-white hover:bg-green-700'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              </td>
                              <td className="px-3 py-2 text-gray-600">{order.VoucherNo || '-'}</td>
                              <td className="px-3 py-2 text-gray-600">{order.Client?.Name || '-'}</td>
                              <td className="px-3 py-2 text-gray-600">{order.Customer?.Name || '-'}</td>
                              <td className="px-3 py-2 text-gray-600">{order.CustomerAddress?.Location?.Name || '-'}</td>
                              <td className="px-3 py-2 text-gray-600">{order.CustomerAddress?.Emirate?.Name || '-'}</td>
                              <td className="px-3 py-2 text-gray-600">{order.CustomerAddress?.Location?.Area?.Name || '-'}</td>
                              <td className="px-3 py-2 text-gray-600">{order.Attempts || '-'}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setShowSmsModal(false);
                  setSmsConfig({
                    NeedClientSms: false,
                    NeedCustomerSms: false,
                    ClientSms: '',
                    CustomerSms: ''
                  });
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitSms}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Submit
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PickupAllocation;

